import { InMemoryCacheConfig, TypePolicy, ApolloLink } from '@apollo/client';
import { getMainDefinition } from '@apollo/client/utilities';
import { GQL_GATEWAY_BASE_URL, SSR_GQL_GATEWAY_BASE_URL } from '@reverbdotcom/env';

export const NON_CACHEABLE_TYPENAMES = Object.freeze([
  'OrderCollection',
  'ListingCollection',
  'WatchCollection',
  'MyGearCollectionItems',
]);

function nonCacheable(typename: string): boolean {
  return NON_CACHEABLE_TYPENAMES.indexOf(typename) > -1;
}

/**
 * Use this for response field types that fit this criteria:
 * 1. Do not have an `id`, `uuid`, or `_id` field.
 * 2. Are nested within other objects that do have an `id`, `uuid`, or `_id` field.
 */
const MERGE_EXISTING_AND_INCOMING_FIELDS = { merge: true };

/**
 * Use this for CoreQL response wrappers and value objects, which have no `_id`
 * in their SDL. Apollo consults `dataIdFromObject` only when a type policy
 * leaves the key function unset, so `keyFields` is what quiets the Node check
 * below; `merge: true` would not, since it leaves the key function alone.
 */
const NO_IDENTITY: TypePolicy = { keyFields: false };

/**
 * Shared settings for apollo's InMemoryCache for use in client and server JS.
 */
export const cacheOptions: InMemoryCacheConfig = {
  possibleTypes: {
    FeedEntry: ['Article', 'Listing'],
    NotificationStreamItem: [
      'OrderDeliveredStreamItem',
      'PromotionStreamItem',
      'SavedShopSaleStreamItem',
      'SiteBannerStreamItem',
      'WatchedItemSoldStreamItem',
      'WatchedItemStreamItem',
    ],
  },
  typePolicies: {
    core_apimessages_CheckoutPaymentMethod: MERGE_EXISTING_AND_INCOMING_FIELDS,
    core_apimessages_Condition: MERGE_EXISTING_AND_INCOMING_FIELDS,
    core_apimessages_CSPInventory: MERGE_EXISTING_AND_INCOMING_FIELDS,
    core_apimessages_CSPReviews: MERGE_EXISTING_AND_INCOMING_FIELDS,
    core_apimessages_FeedbacksResponse: MERGE_EXISTING_AND_INCOMING_FIELDS,
    core_apimessages_Feedback: MERGE_EXISTING_AND_INCOMING_FIELDS,
    core_apimessages_EstimatedMonthlyPayment: MERGE_EXISTING_AND_INCOMING_FIELDS,
    core_apimessages_Link: MERGE_EXISTING_AND_INCOMING_FIELDS,
    core_apimessages_ListingPricing: MERGE_EXISTING_AND_INCOMING_FIELDS,
    core_apimessages_Money: MERGE_EXISTING_AND_INCOMING_FIELDS,
    core_apimessages_PreorderInfo: MERGE_EXISTING_AND_INCOMING_FIELDS,
    core_apimessages_ReturnPolicy: MERGE_EXISTING_AND_INCOMING_FIELDS,
    core_apimessages_Ribbon: MERGE_EXISTING_AND_INCOMING_FIELDS,
    core_apimessages_ShippingPrices: MERGE_EXISTING_AND_INCOMING_FIELDS,
    core_apimessages_ListingCertifiedPreOwned: MERGE_EXISTING_AND_INCOMING_FIELDS,
    mparticle_ProfileResponse: MERGE_EXISTING_AND_INCOMING_FIELDS,
    reverb_pricing_Money: MERGE_EXISTING_AND_INCOMING_FIELDS,
    reverb_pricing_PriceRecommendation: MERGE_EXISTING_AND_INCOMING_FIELDS,
    core_apimessages_CheckoutOrder: { keyFields: ['listingId'] },
    rql_MyCart: MERGE_EXISTING_AND_INCOMING_FIELDS,
    ListingRenewalCopy: { keyFields: false },
    ListingRenewalCopyGeneric: { keyFields: false },
    FeeLineItem: { keyFields: false },
    EstimatedEarnings: { keyFields: false },
    ListingRenewalEligibility: { keyFields: false },
    TradeInEligibility: { keyFields: false },
    TradeInAppraisal: { keyFields: false },
    TradeInStorefront: { keyFields: ['id'] },
    SearchDocument: { keyFields: false },
    SearchFacetInfo: { keyFields: false },
    SearchQueryInfo: { keyFields: ['executedQuery'] },
    SearchResult: { keyFields: false },
    SearchResultFacet: { keyFields: false },
    SearchResultFacetGroup: { keyFields: false },
    SearchResultInfo: { keyFields: false },
    SearchPagination: { keyFields: false },
    SortOption: { keyFields: false },
    Sorts: { keyFields: ['key'] },
    PromotionInfo: { keyFields: false },
    NotificationCountsResponse: NO_IDENTITY,
    NotificationActionsResponse: NO_IDENTITY,
    NotificationTasksResponse: NO_IDENTITY,
    NotificationStreamResponse: NO_IDENTITY,
    NotificationAction: NO_IDENTITY,
    NotificationActionLink: NO_IDENTITY,
    NotificationTask: NO_IDENTITY,
    NotificationEventTracking: NO_IDENTITY,
    MarkNotificationStreamItemsReadPayload: NO_IDENTITY,
    MarkAllNotificationStreamItemsReadPayload: NO_IDENTITY,
    // Selecting one of these without `id` throws rather than embedding, so a
    // new stream query has to include it.
    WatchedItemStreamItem: { keyFields: ['id'] },
    WatchedItemSoldStreamItem: { keyFields: ['id'] },
    SavedShopSaleStreamItem: { keyFields: ['id'] },
    OrderDeliveredStreamItem: { keyFields: ['id'] },
    PromotionStreamItem: { keyFields: ['id'] },
    SiteBannerStreamItem: { keyFields: ['id'] },
    Query: {
      fields: {
        feeds: {
          keyArgs: ['input', ['feedId']],
          merge(existing, incoming) {
            if (!existing) {
              return incoming;
            }
            return {
              ...incoming,
              components: [...existing.components, ...incoming.components],
            };
          },
        },
      },
    },
  },
  dataIdFromObject: (result: any) => {
    if (result.__typename) {
      if (nonCacheable(result.__typename)) {
        return null;
      }

      // Check to see if we're selecting a Node type via the naming convention
      // established in RQL. Types like Listing are Nodes, but types like api_messages_Listing
      // are not.
      //
      // Having conflicting or missing IDs for objects can result in failures to write to the apollo
      // cache. Node should _always_ have an _id field.
      if (/^[A-Z]/.test(result.__typename) && result.__typename != 'Mutation') {
        if (!result._id) {
          console.error(`A query returned a ${result.__typename} without an _id field. This can cause Apollo to fail to write this object to the cache`);
        }
      }

      if (result._id !== undefined && result._id !== null && result._id !== '') {
        return `${result.__typename}:${result._id}`;
      }

      if (result.uuid !== undefined && result.uuid !== null && result.uuid !== '') {
        return `${result.__typename}:uuid:${result.uuid}`;
      }

      if (result.id !== undefined && result.id !== null && result.id !== '') {
        return `${result.__typename}:id:${result.id}`;
      }
    }

    return null;
  },
};

function coercePrimitive(val, kind) {
  let newVal;

  switch (kind) {
    case 'String':
      newVal = String(val);
      break;
    case 'Boolean':
      newVal = (String(val) === 'true');
      break;
    case 'Int':
      newVal = parseInt(val, 10);
      break;
    case 'Float':
      newVal = parseFloat(val);
      break;
    default:
      newVal = val;
  }

  return newVal;
}

// cms-api does not validate the input type, however GraphQL 14
// started validating input primitives. This normally would
// be caught via TS, but these values are not known until
// runtimes and cms-api provides no contracts for these values.
export const apolloInputCoercionLink = new ApolloLink((operation, forward) => {
  const coercedVals = {};
  const q = getMainDefinition(operation.query);
  q.variableDefinitions?.forEach((def) => {
    const name = def.variable.name.value;
    const val = operation.variables[name];
    if (!val) { return; }

    // coerce just top level primitive values
    // def.type.kind is ListType for arrays, but
    // have more complicated parsing rules that
    // we hopefully do not need
    if (def.type.kind === 'NamedType') {
      const kind = def.type.name.value;
      coercedVals[name] = coercePrimitive(val, kind);
    }
  });

  operation.variables = {
    ...operation.variables,
    ...coercedVals,
  };

  return forward(operation);
});

export const GRAPHQL_OPERATION_HEADER = 'X-GraphQL-Operation';

export const graphqlOperationHeaderLink = new ApolloLink((operation, forward) => {
  if (operation.operationName) {
    const definition = getMainDefinition(operation.query);
    const operationType = definition.kind === 'OperationDefinition' ? definition.operation : 'query';

    operation.setContext(({ headers = {} }) => ({
      headers: {
        ...headers,
        [GRAPHQL_OPERATION_HEADER]: `${operationType} ${operation.operationName}`,
      },
    }));
  }

  return forward(operation);
});

export function apolloClientUri(): string {
  return GQL_GATEWAY_BASE_URL;
}

export function ssrApolloClientUri(): string {
  return SSR_GQL_GATEWAY_BASE_URL;
}
